import { detachSdkSessionState, typeSafeFilterObject } from '@/helpers/utils'
import { logger } from '@/logger/createLogger'
import { useCapabilitiesStore } from '@/stores/capabilities'
import { useMainStore } from '@/stores/main'
import type {
  CallSession,
  CallLayoutChangedEventParams,
  CallJoinedEventParams,
  CallUpdatedEventParams,
  DialParams,
} from '@signalwire/client'
import { create, type StateCreator } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { useCallStore } from './call'

interface EventsCallBacks {
  onDestroy?: (roomSession: CallSession) => void
  onJoined?: (event: CallJoinedEventParams) => void
  onLayoutChanged?: (event: CallLayoutChangedEventParams) => void
  onMediaConnected?: () => void
  onUpdated?: (event: CallUpdatedEventParams) => void
}

interface Actions {
  actions: {
    _startSession: (
      roomSession: CallSession,
      eventsCallback?: EventsCallBacks,
    ) => Promise<void>
    allowLeaveCall: (callback: () => boolean) => void
    getRoomSession: () => CallSession
    join: (
      params: DialParams,
      eventsCallback?: EventsCallBacks,
    ) => Promise<CallSession | undefined>
    leave: () => Promise<{ error: unknown; success: boolean }>
    listenCallLeft: (callback: () => void) => void
    listenCallStarted: (callback: () => void) => void
    setAttachedTo: (attachedTo: string | undefined) => void
  }
}

interface State {
  afterCallLeaveFn?: () => void
  afterCallStartFn?: () => void
  attachedTo: string | undefined
  callLeft?: () => void
  roomSession: CallSession | undefined
  shouldLeaveCall?: () => boolean
}

type Store = Actions & State

const initialState: State = {
  attachedTo: undefined,
  roomSession: undefined,
}

const stateCreatorFn: StateCreator<Store> = (set, get) => ({
  ...initialState,
  actions: {
    _startSession: async (
      roomSession: CallSession,
      eventsCallback?: EventsCallBacks,
    ) => {
      const { afterCallStartFn, callLeft } = get()

      roomSession.on('media.connected', () => {
        logger.debug('Media Connected')
        eventsCallback?.onMediaConnected?.()
      })

      roomSession.on('room.updated', event => {
        logger.debug('Room updated', event)
        eventsCallback?.onUpdated?.(event)
      })

      roomSession.on('destroy', event => {
        logger.debug('Room destroyed')
        eventsCallback?.onDestroy?.(event)
        callLeft?.()
      })

      roomSession.on('layout.changed', event => {
        eventsCallback?.onLayoutChanged?.(event)
      })

      roomSession?.on('call.joined', event => {
        // @ts-expect-error FIXME we need to expose this on the SDK?
        set({ attachedTo: roomSession.options.destinationNumber as string })

        // Save the capabilities in the Capabilities store
        const { setCapabilities } = useCapabilitiesStore.getState().actions
        const capabilities = event.capabilities
        setCapabilities(capabilities)

        // Call the call joined callbacks
        eventsCallback?.onJoined?.(event)
        afterCallStartFn?.()
      })

      await roomSession.start()
    },
    allowLeaveCall: (callback: () => boolean) => {
      set({ shouldLeaveCall: callback })
    },
    getRoomSession: () => {
      const { roomSession } = get()
      if (!roomSession) {
        throw new Error('Room session not defined')
      }
      return roomSession
    },
    join: async (params: DialParams, eventsCallback?: EventsCallBacks) => {
      const { client } = useMainStore.getState()
      const { attachedTo } = get()
      const { _startSession } = get().actions
      const {
        currentNotification,
        actions: { setCurrentNotification },
      } = useCallStore.getState()

      try {
        if (!client) {
          throw new Error('Client is not defined')
        }

        let roomSession: CallSession
        if (currentNotification?.invite) {
          roomSession = currentNotification.invite.accept(params)
          // TODO: Verify if notification cleanup can be moved to Call store (or elsewhere?)
          setCurrentNotification(undefined)
        } else if (attachedTo) {
          roomSession = await client.reattach(params)
        } else {
          roomSession = await client.dial(params)
        }

        await _startSession(roomSession, eventsCallback)

        // NOTE: important - save the room session only after the call.start() is resolved
        // so that call.hangup() can be called onRoomLeave() without throwing an error
        // or so that "ghost" room is not left behind if the user navigates away from the room
        set({ roomSession })

        return roomSession
      } catch (error) {
        // TODO: Handle "403 Forbidden - Room locked" error
        logger.error('Error joining room: ', error)
        set({ attachedTo: undefined })
        throw error
      }
    },
    leave: async () => {
      const result: { error: unknown; success: boolean } = {
        error: undefined,
        success: false,
      }
      const { roomSession, shouldLeaveCall } = get()
      // if we have a beforeCallLeave hooks to check,
      // we should cancel the hangup if that return false
      if (shouldLeaveCall && !shouldLeaveCall()) {
        logger.warn('beforeCallLeaveFn prevented hangup')
        return result
      }

      set({ attachedTo: undefined })
      try {
        await roomSession?.hangup()
        result.success = true
      } catch (error) {
        logger.error('Error hanging up room session', error)
        result.error = error
      }
      detachSdkSessionState()
      set({ roomSession: undefined })
      logger.debug('Session detached')
      return result
    },
    listenCallLeft: (callback: () => void) => {
      set({ callLeft: callback })
    },
    listenCallStarted: (callback: () => void) => {
      set({ afterCallStartFn: callback })
    },
    setAttachedTo: (attachedTo: string | undefined) => {
      set({ attachedTo })
    },
  },
})

const partialize = (state: State) => {
  type KeysToPersist = (typeof persistKeys)[number]
  const persistKeys = ['attachedTo'] as const

  return typeSafeFilterObject({ ...state }, key =>
    persistKeys.includes(key as KeysToPersist),
  ) as Store
}

export const useRoomSessionStore = create<Store>()(
  persist<Store>(stateCreatorFn, {
    merge: (persistedState, currentState) => {
      const mergedState = { ...currentState, ...(persistedState as State) }
      if (mergedState.attachedTo) {
        const path = window.location.pathname + window.location.search
        // TODO: Find a better way to compare two resource destination addresses.
        // if the user is in another route, we need to detach from the previous session
        if (path !== `/room${mergedState.attachedTo}`) {
          mergedState.attachedTo = undefined
          // workaround until the SDK has a detach
          detachSdkSessionState()
        }
      }
      return mergedState
    },
    name: 'sw_puc_attached',
    partialize: state => partialize(state),
    storage: createJSONStorage(() => sessionStorage),
  }),
)
export const useRoomSessionStoreActions = () =>
  useRoomSessionStore.getState().actions

export type CallStoreType = typeof useRoomSessionStore
